import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Brand button. Variants: primary (filled), secondary (outline), ghost (text-only).
 * Sizes: md (default), lg.
 * Renders <a> for href, <Link> for `to`, <button> otherwise.
 */
export const BrandButton = React.forwardRef(function BrandButton(
  {
    as,
    to,
    href,
    variant = 'primary',
    size = 'md',
    className = '',
    children,
    icon: Icon,
    iconRight: IconRight,
    ...rest
  },
  ref
) {
  const base =
    'inline-flex items-center justify-center gap-2 font-medium rounded-pill ' +
    'transition-[background-color,color,border-color,transform] duration-150 ease-out-quart ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-bg';

  const sizes = {
    md: 'h-11 px-5 text-[15px]',
    lg: 'h-14 px-7 text-[16px]',
  };

  const variants = {
    primary:
      'bg-brand text-white hover:bg-brand-hover active:translate-y-[1px] disabled:opacity-50',
    secondary:
      'bg-transparent text-ink border border-line hover:border-ink active:translate-y-[1px] disabled:opacity-50',
    ghost:
      'bg-transparent text-ink hover:text-brand disabled:opacity-50',
    dark:
      'bg-ink text-white hover:bg-dark active:translate-y-[1px] disabled:opacity-50',
  };

  const cls = `${base} ${sizes[size]} ${variants[variant]} ${className}`;

  const content = (
    <>
      {Icon && <Icon className="w-[18px] h-[18px]" aria-hidden />}
      <span>{children}</span>
      {IconRight && <IconRight className="w-[18px] h-[18px]" aria-hidden />}
    </>
  );

  if (to) return <Link ref={ref} to={to} className={cls} {...rest}>{content}</Link>;
  if (href) return <a ref={ref} href={href} className={cls} {...rest}>{content}</a>;
  const As = as || 'button';
  return <As ref={ref} className={cls} {...rest}>{content}</As>;
});

export default BrandButton;
